import React, { useState, useCallback, useEffect } from "react";
import { InputComponent, DropdownComponent } from "../../core/Forms";
import { Button, SearchInput } from "../../common";
import { BankData } from "../../../types";
import { clearFieldError } from "../../../utils/handleErrors";
import {
  ValidationError,
  validateBankForm,
} from "../../../modules/settings/settings";
import { useBankAccountSearch } from "../../../hooks/bankAccount/useBankSearch";
import { Bank } from "../../../@types";
import { CloseIcon, SearchIcon } from "../../../assets/icons";
import { verifyBankAccount } from "../../../backend-services";
import BankToast from "./BankToast";
import { useCompanyStore } from "../../../state/useCompanyStore";

interface BankAccountFormProps {
  initialData: BankData;
  edit?: boolean;
  isLoading?: boolean;
  onSubmit: (data: BankData) => Promise<JSX.Element | void>;
  bankName?: Bank | null;
  onClose?: () => void;
  title: string;
  fromToDos?: boolean;
}

export const BankAccountForm: React.FC<BankAccountFormProps> = ({
  initialData,
  edit = false,
  onSubmit,
  onClose,
  isLoading,
  title,
  fromToDos,
}) => {
  const [formData, setFormData] = useState(initialData);
  const [validationResults, setValidationResults] = useState<ValidationError>({
    bankName: "",
    accountNumber: "",
    accountName: "",
  });
  const [selectedBank, setSelectedBank] = useState<Bank | null>(null);
  const [loading, setLoading] = useState(false);
  const {
    data: bankAccount,
    isSearching,
    handleSearch: handleBankAccountSearch,
  } = useBankAccountSearch();
  const userStore = useCompanyStore();
  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;

      if (name === "accountNumber") {
        const numericValue = value.replace(/\D/g, "");
        const restrictedValue = numericValue.slice(0, 10);

        setFormData((prevFormData) => ({
          ...prevFormData,
          [name]: restrictedValue,
        }));
      } else {
        setFormData((prevFormData) => ({
          ...prevFormData,
          [name]: value,
        }));
      }

      clearFieldError(name, validationResults, setValidationResults);
    },
    [formData, validationResults],
  );
  const buttonText = edit ? "Save Changes" : fromToDos ? "Save" : "Add";

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const isValid = validateBankForm(formData, setValidationResults);
      if (isValid) {
        setLoading(true);
        try {
          await onSubmit(formData);
        } finally {
          setLoading(false);
        }
      }
    },
    [formData, onSubmit, setValidationResults],
  );

  const handleSetBankAccount = (selectedBank: Bank | null) => {
    if (selectedBank) {
      setFormData((prevData) => ({
        ...prevData,
        bankName: selectedBank.name,
      }));
      setSelectedBank(selectedBank);
      setValidationResults((prevErrors) => ({
        ...prevErrors,
        bankName: "",
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        bankName: "",
      }));
      setSelectedBank(null);
    }
    clearFieldError("selectedBank", validationResults, setValidationResults);
  };

  const selected = selectedBank
    ? { name: selectedBank.name, slug: selectedBank.name }
    : null;

  const verifyAccount = async (bankName: string, accountNumber: string) => {
    try {
      const account = await verifyBankAccount({ accountNumber, bankName });
      return account;
    } catch (e: any) {}
  };
  const [isVerifying, setIsVerifying] = React.useState(userStore.company.country == "NG" ? true : false);
  const [verificationResult, setVerificationResult] = React.useState<
    string | null
  >(null);
  const DEBOUNCE_DELAY = 500;
  React.useEffect(() => {
    if (formData.accountNumber?.length == 10 && formData?.bankName && userStore.company.country == "NG") {
      const handler = setTimeout(async () => {
        setIsVerifying(true);
        setVerificationResult("Verifying Bank Details...");
        try {
          const result = await verifyAccount(
            formData?.bankName,
            formData.accountNumber,
          );
          setFormData({ ...formData, accountName: result?.accountName! });
          if (result && result.accountName) {
            setVerificationResult("Bank verified Successfully.");
            setIsVerifying(false);
          } else {
            setVerificationResult("Invalid bank details.");
          }
        } catch (error: any) {
          setVerificationResult("Invalid bank details.");
        }
      }, DEBOUNCE_DELAY);

      return () => clearTimeout(handler);
    } else {
      setVerificationResult(null);
    }
  }, [formData.accountNumber]);


  return (
    <form onSubmit={handleSubmit}>
      <div className="bg-gray-900 bg-opacity-50 fixed top-0 right-0 left-0 z-50 h-[100vh]">
        <div className="hidden md:flex justify-center items-center h-full w-full">
          <div className="text-[#212121] w-[300px] md:w-[521px] md:p-5 bg-white rounded-lg">
            <div className="flex items-center justify-between mb-5 md:mb-8">
              <h3 className="text-[18px] md:text-[20px] leading-6 font-semibold">
                {title}
              </h3>
              {!fromToDos && (
                <button
                  aria-label="Close"
                  className="flex items-center justify-center h-11 w-11 pr-[1em]"
                  type="button"
                  onClick={onClose}
                >
                  <CloseIcon color="#212121" />
                </button>
              )}
            </div>
            <div className="space-y-4 md:space-y-5">
              {userStore.company.country == "NG" ? (
                <div className=" w-full">
                  <div className="flex gap-1 items-center text-[14px] md:text-[16px]">
                    <p className="text-[14px] leading-5 text-[#475467]">
                      Bank Name
                    </p>
                    <p className="leading-5 text-xs text-[#98A2B3]">
                      (Required)
                    </p>
                  </div>
                  <SearchInput
                    data={bankAccount}
                    placeholder={
                      formData?.bankName ? formData?.bankName : "Bank Name"
                    }
                    displayKey="name"
                    isLoading={isSearching}
                    value={formData?.bankName}
                    key={initialData.bankName}
                    clearIconClass="mr-[0.5em] md:mt-[0.8em]"
                    dropdownClassName="w-full"
                    dropdownStyle={{
                      maxHeight: "200px",
                      overflowY: "auto",
                    }}
                    emptyMessage={"This bank is not found"}
                    classNames={`w-full border border-gray-300 bg-white h-10 md:h-12 placeholder:text-[16px]  rounded-lg focus:outline-none focus:shadow-outline appearance-none lg:focus:ring-0 leading-normal ${formData?.bankName ? "placeholder:text-black" : "placeholder:text-[#98A2B3]"}`}
                    onSelect={handleSetBankAccount}
                    handleChangeText={handleBankAccountSearch}
                    searchKey={"name"}
                    //@ts-ignore
                    selected={selected}
                    icon={<SearchIcon />}
                    clearIcon={CloseIcon}
                  />
                  {validationResults.bankName && (
                    <div className="text-red-500">
                      {validationResults.bankName}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div className="flex gap-1 items-center text-[14px] md:text-[14px]">
                    <p className="leading-5 text-sm text-[#475467]">
                      Bank Name
                    </p>
                    <p className="leading-5 text-xs text-[#98A2B3]">(Required)</p>
                  </div>
                  <InputComponent
                    placeholder="Bank Name"
                    name="bankName"
                    type="text"
                    value={formData.bankName}
                    handleChange={handleInputChange}
                    className="w-full h-10 md:h-12 placeholder:text-[16px] placeholder:text-[#98A2B3] rounded-lg border-[#D0D5DD]"
                  />
                  {validationResults.bankName && (
                    <div className="text-red-500">
                      {validationResults.bankName}
                    </div>
                  )}
                </div>
              )}
              <div>
                <div className="flex gap-1 items-center text-[14px] md:text-[16px]">
                  <p className="text-[14px] leading-5 text-[#475467]">
                    Account Number
                  </p>
                  <p className="leading-5 text-xs text-[#98A2B3]">(Required)</p>
                </div>
                <InputComponent
                  placeholder="Account Number"
                  name="accountNumber"
                  type="number"
                  value={formData.accountNumber}
                  handleChange={handleInputChange}
                  className="w-full h-10 md:h-12 placeholder:text-[16px] placeholder:text-[#98A2B3] rounded-lg border-[#D0D5DD]"
                />
                {validationResults.accountNumber && (
                  <div className="text-red-500">
                    {validationResults.accountNumber}
                  </div>
                )}
                {verificationResult && (
                  <BankToast color="" type="" message={verificationResult} />
                )}
              </div>
              <div>
                <div className="flex gap-1 items-center text-[14px] md:text-[14px]">
                  <p className="leading-5 text-sm text-[#475467]">
                    Account Name
                  </p>
                  {userStore.company.country !== "NG" && <p className="leading-5 text-xs text-[#98A2B3]">(Required)</p>}
                </div>
                <InputComponent
                  placeholder="Account Name"
                  name="accountName"
                  type="text"
                  value={formData.accountName}
                  disabled={userStore.company.country == "NG"}
                  handleChange={handleInputChange}
                  className="w-full h-10 md:h-12 placeholder:text-[16px] placeholder:text-[#98A2B3] rounded-lg border-[#D0D5DD]"
                />
                {validationResults.accountName && (
                  <div className="text-red-500">
                    {validationResults.accountName}
                  </div>
                )}
              </div>
            </div>

            <div className="w-full gap-[5%] flex justify-between mt-8 md:mt-10">
              <button
                className={`text-[#044E97] border ${
                  fromToDos ? "border-[#EAECF0]" : "border-[#044E97]"
                } w-full h-12 rounded-md font-semibold`}
                onClick={onClose}
              >
                Cancel
              </button>
              <Button
                type="submit"
                isLoading={loading}
                variant={"secondary"}
                // disabled={loading || isLoading}
                disabled={isVerifying || loading || isLoading}
                font="semibold"
                size="full"
              >
                {loading ? "Loading..." : buttonText}
              </Button>
            </div>
          </div>
        </div>

        <div
          className={`md:hidden fixed bottom-0 w-full z-50 px-4 p-4 pt-6 bg-white rounded-t-[16px]`}
        >
          <div className="text-[#212121]">
            <div className="flex items-center justify-between mb-5 md:mb-10">
              <h3 className="text-[18px] md:text-[24px] font-medium">
                {title}
              </h3>

              <button
                aria-label="Close"
                className="flex items-center justify-center  h-11 w-11 pr-[1em]"
                type="button"
                onClick={onClose}
              >
                <CloseIcon color="#212121" width="24" height="24" />
              </button>
            </div>
            <div className="space-y-4 md:space-y-6">
              {userStore.company.country == "NG" ? (
                <div className=" w-full">
                  <div className="flex space-x-3 items-center text-[14px] md:text-[16px] mb-1 md:mb-2">
                    <p>Bank Name</p>
                    <p className="leading-5 text-[#98A2B3] text-sm">
                      (Required)
                    </p>
                  </div>
                  <SearchInput
                    data={bankAccount}
                    placeholder="Search Bank Account"
                    displayKey="name"
                    isLoading={isSearching}
                    value={
                      formData?.bankName
                        ? formData.bankName
                        : initialData?.bankName
                    }
                    key={initialData.bankName}
                    clearIconClass="mr-[0.5em] md:mt-[0.8em]"
                    dropdownClassName="w-full"
                    dropdownStyle={{
                      maxHeight: "150px",
                      overflowY: "auto",
                    }}
                    emptyMessage={"This bank is not found"}
                    classNames="w-full border border-gray-300 bg-white h-10 md:h-12 placeholder:text-[15px] rounded-lg focus:outline-none focus:shadow-outline appearance-none lg:focus:ring-0 leading-normal"
                    onSelect={handleSetBankAccount}
                    handleChangeText={handleBankAccountSearch}
                    searchKey={"name"}
                    //@ts-ignore
                    selected={selected}
                    icon={<SearchIcon />}
                    clearIcon={CloseIcon}
                  />
                  {validationResults.bankName && (
                    <div className="text-red-500">
                      {validationResults.bankName}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div className="flex gap-1 items-center text-[14px] md:text-[14px]">
                    <p className="leading-5 text-sm text-[#475467]">
                      Bank Name
                    </p>
                    <p className="leading-5 text-xs text-[#98A2B3]">(Required)</p>
                  </div>
                  <InputComponent
                    placeholder="Bank Name"
                    name="bankName"
                    type="text"
                    value={formData.bankName}
                    handleChange={handleInputChange}
                    className="w-full h-10 md:h-12 placeholder:text-[16px] placeholder:text-[#98A2B3] rounded-lg border-[#D0D5DD]"
                  />
                  {validationResults.bankName && (
                    <div className="text-red-500">
                      {validationResults.bankName}
                    </div>
                  )}
                </div>
              )}
              <div>
                <div className="flex space-x-3 items-center text-[14px] md:text-[16px] mb-1 md:mb-2 text-[#475467]">
                  <p>Account Number</p>
                  <p className="leading-5 text-[#98A2B3] text-xs">(Required)</p>
                </div>
                <InputComponent
                  placeholder="Account Number"
                  name="accountNumber"
                  type="number"
                  value={formData.accountNumber}
                  handleChange={handleInputChange}
                  className="w-full h-10 md:h-12 placeholder:text-[15px] placeholder:text-[#98A2B3] rounded-lg border-[#D0D5DD]"
                />
                {validationResults.accountNumber && (
                  <div className="text-red-500">
                    {validationResults.accountNumber}
                  </div>
                )}
                {verificationResult && (
                  <BankToast color="" type="" message={verificationResult} />
                )}
              </div>
              <div>
                <div className="flex space-x-3 items-center text-[14px] md:text-[16px] mb-1 md:mb-2 text-[#475467]">
                  <p> Account Name</p>
                 {userStore.company.country !== "NG" && <p className="leading-5 text-xs text-[#98A2B3]">(Required)</p>}
                </div>
                <InputComponent
                  placeholder="Account Name"
                  name="accountName"
                  type="text"
                  value={formData.accountName}
                  handleChange={handleInputChange}
                  disabled={userStore.company.country == "NG"}
                  className="w-full h-10 md:h-12 placeholder:text-[15px] placeholder:text-[#98A2B3] rounded-lg border-[#D0D5DD]"
                />
                {validationResults.accountName && (
                  <div className="text-red-500">
                    {validationResults.accountName}
                  </div>
                )}
              </div>
            </div>

            <div className="w-full gap-[5%] flex justify-between mt-8 md:mt-10">
              <button
                className="text-[#044E97] border border-gray-400 w-full h-12 rounded-md font-semibold "
                onClick={onClose}
              >
                Cancel
              </button>
              <Button
                type="submit"
                isLoading={loading}
                variant={"secondary"}
                disabled={loading || isLoading}
                font="semibold"
                size="full"
              >
                {loading ? "Loading..." : buttonText}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default BankAccountForm;
